<template>
  <v-container id="user_profile_root" fluid>
    <!-- DIALOG -->
    <router-view name="dialog"></router-view>

    <!-- CONTENT -->
    <v-container fluid>
      <v-row lass="mb-6 px-6" align="center" justify="center">
        <v-col>
          <span class="text-h5">
            {{ $t("users.title.profile") }}
            <v-spacer></v-spacer>
          </span>
        </v-col>
      </v-row>

      <v-row>
        <!-- SETTINGS -->
        <v-col>
          <v-card class="pa-6">
            <v-card-title color="black--text">
              <v-icon color="black" class="mr-2">mdi-account-outline</v-icon>
              {{ $t("users.settings.general") }}
            </v-card-title>
            <v-list-item>
              <v-list-item-content>
                <v-form ref="userProfileForm" v-model="userProfileFormValid">
                  <v-text-field
                    id="firstname"
                    v-model="user.firstName"
                    :label="$t('users.content.firstName') + `*`"
                    :rules="[rules.required, rules.minMax]"
                  ></v-text-field>
                  <v-text-field
                    id="lastname"
                    v-model="user.lastName"
                    :label="$t('users.content.lastName') + `*`"
                    :rules="[rules.required, rules.minMax]"
                  ></v-text-field>
                  <v-switch
                    id="userPublic"
                    v-model="user.userPublic"
                    :label="
                      user.userPublic
                        ? $t('users.content.userPublicEnabled')
                        : $t('users.content.userPublicDisabled')
                    "
                  ></v-switch>
                  <v-btn
                    v-if="accountManagementURL"
                    id="accountManagement"
                    text
                    color="primary"
                    target="_blank"
                    :href="accountManagementURL"
                  >
                    <v-icon class="mr-1">mdi-cog-outline</v-icon>
                    {{ $t("users.content.manageAccount") }}
                  </v-btn>
                  <v-btn
                    id="update"
                    class="mt-3 mb-3"
                    color="primary"
                    left
                    :disabled="updateMyUserDisabled"
                    @click="updateMyUser"
                  >
                    <v-icon>mdi-progress-pencil</v-icon>
                    {{ $t("main.update") }}
                  </v-btn>
                </v-form>
              </v-list-item-content>
            </v-list-item>

            <v-card-title color="black--text">
              <v-icon color="black" class="mr-2">mdi-map</v-icon>
              {{ $t("users.content.language") }}
            </v-card-title>
            <v-list-item>
              <v-list-item-content>
                <v-form ref="userLanguageForm">
                  <v-select
                    id="language"
                    v-model="selectedLanguage"
                    :items="languageOptions"
                    :label="$t('users.content.language')"
                    @change="onLanguageChange"
                  ></v-select>
                </v-form>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <div>
                  <span>{{ $t("users.content.roles.label") }}</span>
                  <v-chip-group column>
                    <v-chip v-for="role in rdssRoles" :key="role.value">
                      {{
                        $t(role.text)
                      }}
                    </v-chip>
                  </v-chip-group>
                </div>
              </v-list-item-content>
            </v-list-item>

            <v-divider class="my-6"></v-divider>

            <v-card-title v-if="isVerificationEnabled" color="black--text">
              <v-icon color="black" class="mr-2">mdi-card-account-details</v-icon>

              {{ $t("users.verification.title") }}
            </v-card-title>

            <div
              v-if="isVerificationEnabled"
              id="verification-message"
              class="d-flex align-center"
              :class="userVerified ? 'green--text' : 'red--text'"
            >
              <v-icon :color="userVerified ? 'success' : 'red'">
                {{
                  userVerified ? "mdi-check" : "mdi-close"
                }}
              </v-icon>
              <span>{{
                userVerified
                  ? $t("users.content.userIsVerified")
                  : $t("users.content.userIsNotVerified")
              }}</span>
            </div>

            <v-data-table
              v-if="isVerificationEnabled"
              id="verification-table"
              :headers="verifiedHeaders"
              :items="verifiedFields"
              class="elevation-1 mt-3 mb-3 ml-3 mr-3"
              hide-default-footer
              :item-class="itemClass"
            >
              <template #items="props">
                <td :class="props.item.rowClass">{{ props.item.label }}</td>
                <td :class="props.item.rowClass">{{ props.item.value || "N/A" }}</td>
              </template>
            </v-data-table>

            <v-btn
              v-if="isVerificationEnabled"
              id="reset-verification-button"
              :disabled="!userVerified"
              @click="resetVerification()"
            >
              <v-icon>mdi-restart</v-icon>
              {{ $t("users.verification.resetVerification") }}
            </v-btn>

            <template v-if="deleteAccountURL">
              <v-divider v-if="isVerificationEnabled" class="my-6"></v-divider>
              <v-card-title color="black--text">
                <v-icon color="black" class="mr-2">mdi-close-octagon</v-icon>
                {{ $t("users.content.deleteMyAccount") }}
              </v-card-title>
              <v-card-subtitle class="ma-6 subtitle-1">
                {{ $t("users.content.descriptionDeleteMyAccount") }}
              </v-card-subtitle>

              <v-btn
                v-if="deleteAccountURL"
                id="deleteAccount"
                outlined
                text
                color="error"
                class="mb-3 mt-3"
                target="_blank"
                :href="deleteAccountURL"
              >
                <v-icon class="mr-1">mdi-delete-outline</v-icon>
                {{ $t("users.content.deleteMyAccount") }}
              </v-btn>
            </template>
          </v-card>
        </v-col>

        <v-col>
          <!-- BUSINESS ACCOUNT -->
          <v-card class="pa-6 mb-4">
            <v-card-title>
              <v-icon color="black" class="mr-2">mdi-office-building</v-icon>
              {{ $t("businessAccount.title") }}
            </v-card-title>

            <!-- FREEMIUM STATE (No Business Account) -->
            <v-list-item v-if="!hasBusinessAccount && !businessAccountLoading">
              <v-list-item-content>
                <p class="mb-4">{{ $t("businessAccount.noBusinessAccount") }}</p>

                <v-form ref="businessAccountForm" v-model="businessAccountFormValid">
                  <v-text-field
                    id="ba_name"
                    v-model="businessAccountRequest.name"
                    :label="$t('businessAccount.create.form.name')"
                    :rules="[rules.required]"
                  ></v-text-field>
                  <v-text-field
                    id="ba_email"
                    v-model="businessAccountRequest.email"
                    :label="$t('businessAccount.create.form.email')"
                    :rules="[rules.required, rules.email]"
                  ></v-text-field>
                  <v-text-field
                    id="ba_phone"
                    v-model="businessAccountRequest.phoneNumber"
                    :label="$t('businessAccount.create.form.phoneNumber')"
                  ></v-text-field>

                  <v-btn
                    id="ba_send"
                    class="mt-3"
                    color="primary"
                    :disabled="!businessAccountFormValid || sendingRequest"
                    :loading="sendingRequest"
                    @click="sendBusinessAccountRequest"
                  >
                    <v-icon>mdi-send</v-icon>
                    {{ $t("businessAccount.create.send") }}
                  </v-btn>
                </v-form>
              </v-list-item-content>
            </v-list-item>

            <!-- PREMIUM STATE (With Business Account) -->
            <v-list-item v-if="baID && !businessAccountLoading">
              <v-list-item-content>
                <!-- Business Account Details -->
                <div
                  v-if="businessAccountDetails"
                  id="business-account-details"
                  class="mb-4"
                >
                  <v-simple-table dense>
                    <template #default>
                      <tbody>
                        <tr>
                          <td>
                            <strong>{{ businessAccountDetails.name }}</strong>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <strong>{{ $t("businessAccount.fields.id") }}</strong>
                          </td>
                          <td>{{ businessAccountDetails.id }}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>{{ $t("businessAccount.fields.createdAt") }}</strong>
                          </td>
                          <td>{{ formatDate(businessAccountDetails.createdAt) }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </div>
                <div>
                  <v-btn
                    id="delete_member_btn"
                    class="mt-4"
                    color="error"
                    :disabled="deletingMember"
                    @click="deleteMember(item, $event)"
                  >
                    <v-icon left>mdi-delete</v-icon>
                    {{ $t("businessAccount.removeMeFromBusinessAccount") }}
                  </v-btn>
                </div>
                <div v-if="isBAAdmin" class="mt-4">
                  <v-btn
                    id="ba_admin_link"
                    color="primary"
                    :to="{ name: 'BusinessAccount' }"
                  >
                    <v-icon left>mdi-office-building-cog</v-icon>
                    {{ $t("businessAccount.manageBusinessAccount") }}
                  </v-btn>
                </div>
              </v-list-item-content>
            </v-list-item>

            <!-- Loading State -->
            <v-list-item v-if="businessAccountLoading">
              <v-list-item-content>
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </v-list-item-content>
            </v-list-item>
          </v-card>

          <!-- DEVICES -->
          <v-card v-if="showDevices" class="pa-6">
            <v-card-title>
              <v-icon color="black" class="mr-2">mdi-cellphone-key</v-icon>
              {{ $t("dashboard.devices") }}
            </v-card-title>

            <v-data-table
              id="devices_table"
              must-sort
              :headers="headers"
              hide-default-header
              hide-default-footer
              :items="deviceList"
              :options.sync="pagination"
              :server-items-length="devicesListTotalItems"
              :loading="dataTableLoading"
              :loading-text="$t('dataTable.content.loadingPleaseWait')"
              :no-data-text="$t('dataTable.content.noDataAvailable')"
            >
              <template #[`item.status`]="{ item }">
                <v-chip label :color="getChipStyle(item)">
                  {{ getChipText(item) }}
                </v-chip>
              </template>
              <template #[`item.actions`]="{ item }">
                <v-btn
                  id="devices_table_manage"
                  text
                  class="pa-1"
                  :to="'/user/profile/devices/' + item.id + '/update'"
                >
                  <v-icon> mdi-update </v-icon>
                  {{ $t("dataTable.content.devices.manage") }}
                </v-btn>

                <v-btn
                  id="devices_table_delete"
                  text
                  class="pa-1"
                  @click="deleteDevice(item.id, $event)"
                >
                  <v-icon> mdi-delete </v-icon>
                  {{ $t("dataTable.content.devices.delete") }}
                </v-btn>
              </template>
            </v-data-table>

            <v-btn
              id="devices_table_add"
              text
              color="primary"
              to="/user/profile/devices/add"
            >
              <v-icon>mdi-plus</v-icon>
              {{ $t("dashboard.addDevice") }}
            </v-btn>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>

<script>
import credentials from '@/services/api/credentials'
import devices from '@/services/api/devices'
import seal from '@/services/api/seal.js'
import debounce from 'lodash.debounce'
import { mapGetters } from 'vuex'
import Role from '../../Role'
import { showConfirmationMenu, showToastError, showToastSuccess } from '../../event-bus'
import eventTypes from '@/eventTypes'
import businessAccount from '@/services/api/businessAccount'
import rsspUtil from '@/services/api/rsspUtil'
import userVerification from '@/services/api/userVerification'
import users from '@/services/api/users'
import CommonError from '../CommonError'
import language from './Language'
import UserStatus from './UserStatus'
import defaultRoles from './defaultRoles'

export default {
  name: 'UserProfile',

  data() {
    return {
      userProfileFormValid: false,
      maxCharacters: 255,
      user: {},
      selectedLanguage: language.EN,
      switchUserStatus: false,
      userStatusList: UserStatus,
      setDistinguishedName: false,
      distinguishedName: {},
      rules: {
        required: (v) =>
          !!v || !isNaN(parseFloat(v)) || this.$t('validation.form.requiredField'),
        minMax: (v) =>
          (v && v.length <= this.maxCharacters) ||
          this.$t('validation.form.mustBeLessThan', { max: this.maxCharacters }),
        email: (v) =>
          !v ||
          /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) ||
          this.$t('validation.form.emailMustBeValid')
      },
      pagination: {
        sortBy: ['friendlyName']
      },
      deviceList: [],
      devicesListTotalItems: 0,
      sealStatus: {
        enabled: false,
        secureMode: false,
        activated: false
      },
      showDevices: true,
      changePasswordURL:
        this.$store.state.remoteConfig &&
        this.$store.state.remoteConfig.changePasswordURL,
      deleteAccountURL:
        this.$store.state.remoteConfig && this.$store.state.remoteConfig.deleteAccountURL,
      dataTableLoading: false,
      headers: [
        {
          text: this.$t('dataTable.header.devices.friendlyName'),
          align: 'left',
          value: 'friendlyName'
        },
        {
          text: this.$t('dataTable.header.devices.status'),
          align: 'left',
          value: 'status',
          sortable: false
        },
        {
          text: this.$t('dataTable.header.devices.user'),
          align: 'left',
          value: 'user.name'
        },
        {
          text: this.$t('dataTable.header.actions'),
          align: 'right',
          value: 'actions',
          sortable: false
        }
      ],
      rdssRoles: [],
      verifiedHeaders: [
        { text: this.$t('users.content.verifiedLabel'), value: 'label' },
        { text: this.$t('users.content.verifiedValue'), value: 'value' }
      ],
      userVerified: false,
      userVerificationStatus: {
        verified: false,
        fields: []
      },
      // Business Account
      businessAccountLoading: false,
      hasBusinessAccount: false,
      businessAccountDetails: null,
      businessAccountFormValid: false,
      sendingRequest: false,
      businessAccountRequest: {
        name: '',
        email: '',
        phoneNumber: ''
      },
      isBAAdmin: false,
      deletingMember: false,
      search: '',
      rsspUser: null
    }
  },

  computed: {
    ...mapGetters(['hasRole']),
    isAdmin() {
      return this.hasRole(Role.ADMIN)
    },
    accountManagementURL() {
      if (
        !this.$store.state.remoteConfig ||
        !this.$store.state.remoteConfig.accountManagementURL
      ) {
        return ''
      }
      const baseURL = this.$store.state.remoteConfig.accountManagementURL
      if (!baseURL) {
        return ''
      }
      const isAdminParam = this.isAdmin ? 'isAdmin=1' : 'isAdmin=0'
      const separator = baseURL.includes('?') ? '&' : '?'
      return `${baseURL}${separator}${isAdminParam}`
    },
    languageOptions() {
      return [
        { value: language.EN, text: this.$t('users.content.languages.English') },
        { value: language.HU, text: this.$t('users.content.languages.Hungarian') }
      ]
    },
    sealActivated() {
      return this.sealStatus.enabled && this.sealStatus.activated
    },
    sealEnabled() {
      return this.sealStatus.enabled
    },
    hasRdss() {
      return !!this.$store.state.remoteConfig.rdssApiUrl
    },
    isVerificationEnabled() {
      return !!this.$store.state.remoteConfig.userVerificationEnabled
    },
    verifiedFields() {
      return this.userVerificationStatus.fields.map((field) => {
        const rowClass = !field.optional && !field.value ? 'red--text red lighten-4' : ''
        return {
          label: field.displayName,
          value: field.value || null,
          rowClass
        }
      })
    },
    updateMyUserDisabled() {
      const { firstName, lastName, userPublic } = this.$store.state.currentUser
      return (
        this.user.firstName === firstName &&
        this.user.lastName === lastName &&
        this.user.userPublic === userPublic
      )
    },
    baID() {
      return this.businessAccountDetails && this.businessAccountDetails.id
        ? this.businessAccountDetails.id
        : undefined
    }
  },

  watch: {
    search() {
      this.dataTableLoading = true
      this.searchDeviceWatcher()
    },
    pagination() {
      this.searchDevices()
    }
  },

  async mounted() {
    this.rsspUser = await rsspUtil.joinUserOnRssp()
    this.sealStatus = await seal.getSealStatus()
    await this.initDeviceEventSource()
    await this.getMyUser()
    await this.getVerificationDetails()
    await this.searchDevices()
    await this.checkBusinessAccount()
  },

  methods: {
    searchDeviceWatcher: debounce(function() {
      this.searchDevices()
    }, 500),

    async searchDevices() {
      if (!this.rsspUser) {
        return
      }

      this.dataTableLoading = true
      try {
        const pageRequest = {
          'page-num': this.pagination.page,
          'page-size': this.pagination.itemsPerPage,
          'sort-field': this.pagination.sortBy[0],
          'sort-order': this.pagination.sortDesc[0] ? 'desc' : 'asc'
        }
        const page = await devices.listMyDevices(this.search, pageRequest)
        this.deviceList = page.content
        this.devicesListTotalItems = page.total
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      } finally {
        this.dataTableLoading = false
      }
    },

    async onLanguageChange(newLanguage) {
      const localeRequest = { locale: newLanguage }
      try {
        await users.updateMyLocale(localeRequest)
        await rsspUtil.updateMyLocale(localeRequest)
        this.$nextTick(() => {
          this.setLanguage(newLanguage)
        })
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    async updateMyUser() {
      if (!this.$refs.userProfileForm.validate()) {
        return
      }
      try {
        await users.updateMyUser(this.user)
        this.$store.commit('setCurrentUser', { ...this.user })
        await rsspUtil.updateMyUser(this.user)
        showToastSuccess(this.$t('toastMessage.succesfullyUpdated'))
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    async getMyUser() {
      try {
        this.user = await users.getMyUser()
        this.setDefaultValuesByGetMyUser()
        this.isBAAdmin = this.user.businessAccountRole === 'ADMIN'
        this.rdssRoles = this.user.roles.map((r) => {
          return {
            value: r,
            text: defaultRoles.find((x) => x.value === r).displayTextJsonKey
          }
        })
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    async resetVerification() {
      try {
        this.userVerificationStatus = await userVerification.resetVerification()
        await this.getVerificationDetails()
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    async getVerificationDetails() {
      if (!this.isVerificationEnabled) return
      try {
        this.userVerificationStatus = await userVerification.getUserVerificationStatus()
        this.userVerified = this.userVerificationStatus.verified
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    itemClass(item) {
      return item.rowClass
    },

    setDefaultValuesByGetMyUser() {
      this.switchUserStatus = !!this.user.status.includes(UserStatus.ENABLED)
      this.selectedLanguage = this.user.locale
    },

    async logout() {
      await this.$auth.logout()
    },

    back() {
      this.$router.go(-1)
    },

    async deleteDevice(id, e) {
      const opts = {
        title: this.$t('dialog.confirmation.title'),
        posX: e.clientX,
        posY: e.clientY
      }
      const conf = await showConfirmationMenu(opts)
      if (!conf) {
        return
      }
      try {
        await devices.deleteDevice(id)
        this.searchDevices()
        showToastSuccess(this.$t('toastMessage.succesfullyDeleted'))
      } catch (error) {
        let errorMsg = this.$t('toastMessage.errorWhileRetrievingData')
        if (error.response !== undefined && error.response.data.error !== undefined) {
          const splittedErrorMessage = error.response.data.error_description.split(' ')
          if (error.response.data.error === CommonError.STILL_REFERENCED) {
            errorMsg = this.$t('toastMessage.stillReferenced', {
              0: splittedErrorMessage[0],
              1: splittedErrorMessage[5]
            })
          } else {
            errorMsg = this.getErrorMessage(error)
          }
        } else {
          errorMsg = this.getErrorMessage(error)
        }
        showToastError(errorMsg)
      }
    },

    getChipStyle(item) {
      return item.enabled === true ? 'enabled' : 'inactive'
    },

    getChipText(item) {
      return item.enabled === true
        ? this.$t('dataTable.header.devices.states.enabled')
        : this.$t('dataTable.header.devices.states.inactive')
    },

    async checkCredentials() {
      const resp = await credentials.listMyActivatedCredentials()
      const total = resp.content.filter(
        (elem) => elem.keyPurpose === 'ASYMMETRIC_SIGN' && elem.authMode === 'IMPLICIT'
      ).length
      this.showDevices = total > 0
    },

    async initDeviceEventSource() {
      const token = await this.$auth.getIdToken()
      const eventSourceURL =
        this.$store.state.remoteConfig.rsspApiUrl + '/rssp/v1/events'
      let sse
      try {
        // eslint-disable-next-line
        sse = new EventSourcePolyfill(eventSourceURL, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
      } catch (e) {
        console.error(e)
      }
      sse.addEventListener(eventTypes.DEVICE_CREATED, () => { this.searchDeviceWatcher() })
      sse.addEventListener(eventTypes.DEVICE_UPDATED, () => { this.searchDeviceWatcher() })
      sse.addEventListener(eventTypes.DEVICE_DELETED, () => { this.searchDeviceWatcher() })
    },

    // Business Account methods
    async checkBusinessAccount() {
      this.businessAccountLoading = true
      try {
        const pageRequest = { 'page-size': 5, 'page-num': 1 }
        const response = await businessAccount.listMyBusinessAccounts(pageRequest)
        if (response.content && response.content.length > 0) {
          this.hasBusinessAccount = true
          const id = response.content[0].id
          this.businessAccountLoading = false
          await this.loadBusinessAccountDetails(id)
        } else {
          this.hasBusinessAccount = false
          this.businessAccountDetails = null
        }
      } catch (error) {
        this.hasBusinessAccount = false
        const errorMsg = this.getErrorMessage(error)
        console.error('Error during listing business accounts:', errorMsg)
        showToastError(errorMsg)
      } finally {
        this.businessAccountLoading = false
      }
    },

    async loadBusinessAccountDetails(id) {
      if (this.businessAccountLoading) {
        return
      }
      this.businessAccountLoading = true
      try {
        if (!id) {
          const pageRequest = { 'page-num': 1, 'page-size': 5 }
          const response = await businessAccount.listMyBusinessAccounts(pageRequest)
          if (response.content && response.content.length > 0) {
            id = response.content[0].id
          } else {
            throw new Error('No business account found')
          }
        }
        const details = await businessAccount.getMyBusinessAccountAdv(id)
        this.businessAccountDetails = details
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        const errorMsgFinal = errorMsg || this.$t('businessAccount.detailsLoadFailed')
        console.error('Error loading business account details:', errorMsgFinal)
        showToastError(errorMsgFinal)
      } finally {
        this.businessAccountLoading = false
      }
    },

    async sendBusinessAccountRequest() {
      if (!this.$refs.businessAccountForm.validate()) {
        return
      }
      this.sendingRequest = true
      try {
        const request = {
          name: this.businessAccountRequest.name,
          email: this.businessAccountRequest.email
        }
        if (this.businessAccountRequest.phoneNumber) {
          request.phoneNumber = this.businessAccountRequest.phoneNumber
        }
        await businessAccount.sendBusinessAccountCreationRequest(request)
        showToastSuccess(this.$t('businessAccount.create.success'))
        this.businessAccountRequest = { name: '', email: '', phoneNumber: '' }
        this.$refs.businessAccountForm.reset()
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        const errorMsgFinal = errorMsg || this.$t('businessAccount.create.failed')
        showToastError(errorMsgFinal)
      } finally {
        this.sendingRequest = false
      }
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}.${month}.${day}, ${hours}:${minutes}:${seconds}`
    },

    async deleteMember(member, e) {
      const opts = { title: this.$t('dialog.confirmation.title'), posX: e.clientX, posY: e.clientY }
      const conf = await showConfirmationMenu(opts)
      if (!conf) return
      this.deletingMember = true
      try {
        await businessAccount.removeMember(this.businessAccountDetails.id, this.user.id)
        this.checkBusinessAccount()
        showToastSuccess(this.$t('businessAccount.members.memberDeleted'))
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      } finally {
        this.deletingMember = false
      }
    }
  }
}
</script>

<style scoped>
.green--text {
  color: green !important;
}
.red--text {
  color: red !important;
}
</style>
